// src/fleet-stats/components/AttentionSection.jsx
//
// The single tension point of the page, ordered by newsworthiness:
//   1. In-field robots misbehaving NOW (worst first)
//   2. Robots that recently went offline (compact)
//   3. Client-level trend context
//   4. Standing issues & long-offline inventory (collapsed)
// Rows live inside one container per group — severity is carried by the dot
// and chip, not by per-row colored cards.
import React, { useState } from "react";
import { AlertTriangle, CheckCircle2, TrendingDown, WifiOff } from "lucide-react";
import { C, CARD_BG, CARD_BORDER } from "../constants.js";
import { SectionHeader } from "./SharedUI.jsx";
import { displayRobotName } from "../utils/format.js";

const SEVERITY = {
    critical: { color: C.red, label: "Critical" },
    warning:  { color: C.amber, label: "Warning" },
    info:     { color: C.grey2, label: "Standing" },
};

const INITIAL_VISIBLE = 8;
const OFFLINE_VISIBLE = 6;

function ListContainer({ children }) {
    return (
        <div className="rounded-xl overflow-hidden" style={{ backgroundColor: CARD_BG, border: CARD_BORDER }}>
            {children}
        </div>
    );
}

function AttentionRow({ entry, first, onSelectRobot }) {
    const cfg = SEVERITY[entry.severity] || SEVERITY.info;
    return (
        <button
            onClick={() => onSelectRobot(entry.robotName)}
            className="w-full px-4 py-3 flex items-center gap-3 text-left transition-colors"
            style={{ borderTop: first ? "none" : "1px solid rgba(255,255,255,0.06)", cursor: "pointer" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.04)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            title={`${entry.robotName} — click to drill down`}
        >
            <span className="shrink-0" style={{ color: cfg.color, fontSize: 10 }}>●</span>
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-semibold" style={{ color: C.offWhite }}>
                        {displayRobotName(entry.robotName, entry.siteName)}
                    </span>
                    <span className="text-xs" style={{ color: C.grey2 }}>
                        {entry.client}{entry.siteName ? ` · ${entry.siteName}` : ""}
                    </span>
                </div>
                <div className="text-xs mt-0.5 leading-relaxed" style={{ color: C.grey1 }}>
                    {entry.reasons.map((r) => r.text).join(" · ")}
                </div>
            </div>
            <span
                className="text-xs font-semibold px-2 py-0.5 rounded-full shrink-0"
                style={{ backgroundColor: `${cfg.color}18`, color: cfg.color, border: `1px solid ${cfg.color}44` }}
            >
                {cfg.label}
            </span>
        </button>
    );
}

function OfflineRow({ entry, first, onSelectRobot }) {
    return (
        <button
            onClick={() => onSelectRobot(entry.robotName)}
            className="w-full px-4 py-2 flex items-center gap-2.5 text-left text-xs transition-colors"
            style={{ borderTop: first ? "none" : "1px solid rgba(255,255,255,0.06)", cursor: "pointer" }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.04)")}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            title={`${entry.robotName} — click to drill down`}
        >
            <span style={{ color: C.amber, fontSize: 9 }}>●</span>
            <span className="font-semibold truncate" style={{ color: C.offWhite }}>{displayRobotName(entry.robotName, entry.siteName)}</span>
            <span className="truncate" style={{ color: C.grey2 }}>{entry.client}</span>
            <span className="ml-auto shrink-0 tabular-nums" style={{ color: C.grey2 }}>{entry.reasons[0]?.text}</span>
        </button>
    );
}

function ShowMore({ open, onToggle, children }) {
    return (
        <button
            className="text-sm font-semibold text-left pl-1 transition-colors"
            style={{ color: C.grey2 }}
            onMouseEnter={(e) => (e.currentTarget.style.color = C.neonGreen)}
            onMouseLeave={(e) => (e.currentTarget.style.color = C.grey2)}
            onClick={onToggle}
        >
            {children}
        </button>
    );
}

export function AttentionSection({ inField, recentlyOffline, infoEntries, clientFindings = [], healthyActiveCount = 0, onSelectRobot, sectionRef }) {
    const [showAll, setShowAll] = useState(false);
    const [showAllOffline, setShowAllOffline] = useState(false);
    const [showInfo, setShowInfo] = useState(false);

    const count = inField.length;
    const hasCritical = inField.some((e) => e.severity === "critical");
    const badgeColor = count === 0 ? C.neonGreen : hasCritical ? C.red : C.amber;
    const visible = showAll ? inField : inField.slice(0, INITIAL_VISIBLE);
    const visibleOffline = showAllOffline ? recentlyOffline : recentlyOffline.slice(0, OFFLINE_VISIBLE);
    const allQuiet = count === 0 && recentlyOffline.length === 0 && clientFindings.length === 0;

    return (
        <div className="mb-10" ref={sectionRef}>
            <SectionHeader icon={AlertTriangle}>
                Needs attention
                <span
                    className="ml-2 px-2 py-0.5 rounded-full text-xs font-bold"
                    style={{ backgroundColor: `${badgeColor}22`, color: badgeColor, border: `1px solid ${badgeColor}44` }}
                >
                    {count}
                </span>
            </SectionHeader>

            {allQuiet && (
                <div
                    className="rounded-xl px-4 py-3 mb-2 flex items-center gap-2 text-sm"
                    style={{ backgroundColor: CARD_BG, border: CARD_BORDER, color: C.grey1 }}
                >
                    <CheckCircle2 size={16} style={{ color: C.neonGreen }} />
                    All {healthyActiveCount} active robots healthy — no exceptions found.
                </div>
            )}

            {!allQuiet && (
                <div className="flex flex-col gap-2">
                    {count > 0 && (
                        <ListContainer>
                            {visible.map((entry, i) => (
                                <AttentionRow key={entry.robotId} entry={entry} first={i === 0} onSelectRobot={onSelectRobot} />
                            ))}
                        </ListContainer>
                    )}
                    {inField.length > INITIAL_VISIBLE && (
                        <ShowMore open={showAll} onToggle={() => setShowAll((v) => !v)}>
                            {showAll ? "Show fewer" : `Show all ${inField.length} robots`}
                        </ShowMore>
                    )}

                    {recentlyOffline.length > 0 && (
                        <>
                            <div className="flex items-center gap-2 mt-3 mb-0.5 text-xs font-semibold uppercase tracking-wide" style={{ color: C.grey2 }}>
                                <WifiOff size={13} />
                                Went offline in the last 30 days ({recentlyOffline.length})
                            </div>
                            <ListContainer>
                                {visibleOffline.map((entry, i) => (
                                    <OfflineRow key={entry.robotId} entry={entry} first={i === 0} onSelectRobot={onSelectRobot} />
                                ))}
                            </ListContainer>
                            {recentlyOffline.length > OFFLINE_VISIBLE && (
                                <ShowMore open={showAllOffline} onToggle={() => setShowAllOffline((v) => !v)}>
                                    {showAllOffline ? "Show fewer" : `Show all ${recentlyOffline.length}`}
                                </ShowMore>
                            )}
                        </>
                    )}

                    {/* Client-level trends are context, not actionable rows */}
                    {clientFindings.map((f, i) => (
                        <div
                            key={`client-${i}`}
                            className="rounded-xl px-4 py-3 mt-2 flex items-start gap-3"
                            style={{ backgroundColor: CARD_BG, border: CARD_BORDER }}
                        >
                            <TrendingDown size={15} className="mt-0.5 shrink-0" style={{ color: C.amber }} />
                            <div className="min-w-0">
                                <div className="text-sm font-semibold" style={{ color: C.offWhite }}>{f.title}</div>
                                {f.detail && <div className="text-xs mt-1" style={{ color: C.grey1 }}>{f.detail}</div>}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {infoEntries.length > 0 && (
                <div className="mt-3 flex flex-col gap-2">
                    <ShowMore open={showInfo} onToggle={() => setShowInfo((v) => !v)}>
                        {showInfo ? "Hide" : "Show"} {infoEntries.length} standing issues (quiet &gt;3 weeks / offline &gt;30 days / low utilization)
                    </ShowMore>
                    {showInfo && (
                        <ListContainer>
                            {infoEntries.map((entry, i) => (
                                <AttentionRow key={entry.robotId} entry={entry} first={i === 0} onSelectRobot={onSelectRobot} />
                            ))}
                        </ListContainer>
                    )}
                </div>
            )}
        </div>
    );
}
